<script setup lang="ts">
import Album from './Components/Album.vue'
import Headline from './Components/Headline.vue'
import axios from 'axios'

import UpsellBanner from '@/components/UpsellBanner.vue'
import LegacyDashboardBanner from '@/components/dashboard/LegacyDashboardBanner.vue'
import useSessionStorage from '@/composables/sessionStorage'
import type { dashboardStatisticsParamsInterface } from '@/types/dashboard'
import ChartStepper from './Components/ChartStepper.vue'
import DashboardBanner from './Components/DashboardBanner.vue'
import HourlyVisits from './Components/HourlyVisits.vue'
import NewMembersChart from './Components/NewMembersChart.vue'
import RewardsChart from './Components/RewardsChart.vue'
import VisitorsPerDayChart from './Components/VisitorsPerDayChart.vue'

const { t } = useI18n()
const router = useRouter()
const { user, merchantLogo } = useLocalStorage()
const { subtractDate, formatDate } = useDate()
const isLoaded = ref(false)
const dateValue = ref([subtractDate(6, 'day', 'DD-MM-YYYY'), formatDate({ toFormat: 'DD-MM-YYYY' })])
const displayPongoInStoreChart = ref(0)
const { dashboardItem } = useSessionStorage()
const { selectedStore } = useStore()
const { formatNumber } = useNumber()

const inStoreLabels = ref({
  visitors: {
    title: t('pages.dashboard.dashboardPage.inStore.visitors'),
    value: 'visitorsNumber',
    trend: 'visitorsTrend'
  },
  newMembers: {
    title: t('pages.dashboard.dashboardPage.inStore.newMembers'),
    value: 'enrolledNumber',
    trend: 'enrolledTrend'
  },
  rewards: {
    title: t('pages.dashboard.dashboardPage.inStore.rewards'),
    value: 'rewardsClaimed',
    trend: 'rewardsClaimedTrend'
  }
})
const iconCampaignSms = computed(() => `${import.meta.env.VITE_AWS_STORAGE_URL}/dashboard/campagne_SMS.png`)
const iconContest = computed(() => `${import.meta.env.VITE_AWS_STORAGE_URL}/dashboard/jeu_concours.png`)
const iconSurvey = computed(() => `${import.meta.env.VITE_AWS_STORAGE_URL}/dashboard/survey.png`)
const iconAutopilot = computed(() => `${import.meta.env.VITE_AWS_STORAGE_URL}/dashboard/autopilot.png`)
const iconReview = computed(() => `${import.meta.env.VITE_AWS_STORAGE_URL}/dashboard/avis.png`)
const iconRewards = computed(() => `${import.meta.env.VITE_AWS_STORAGE_URL}/dashboard/fidelite.png`)
const pageTitle = computed(() => {
  return {
    title: t('pages.dashboard.dashboardPage.title', {
      merchantName: user.value?.name
    }),
    description: 'pages.dashboard.dashboardPage.description',
    img: merchantLogo.value
  }
})

const data = computed(() => {
  return dashboardItem.value
})

const noData = computed(() => {
  return {
    background: '/images/dashboard/noData/background.png',
    pattern: '/images/dashboard/noData/pattern.png'
  }
})
const autopilotsRanking = computed(() => {
  const autopilots = dashboardItem.value.autopilot
  const array = []

  autopilots.forEach(
    (
      autopilot: {
        name: string
        trigger: string
        autopilotsConversionRate: string
        is_legacy: boolean
      },
      index: number
    ) => {
      array.push({
        rank: index + 1,
        name: autopilot.is_legacy ? t(`global.autopilots.${autopilot.trigger}.name`) : autopilot.name,
        count: `${removeDecimals(autopilot.autopilotsConversionRate, 0)}%`
      })
    }
  )

  return {
    length: autopilots.length,
    data: array
  }
})

const rewardsRanking = computed(() => {
  const rewards = dashboardItem.value.rewards
  const array = []
  rewards.forEach(
    (
      reward: {
        name: string
        rewardsGiven: number
      },
      index: number
    ) => {
      array.push({
        rank: index + 1,
        name: reward.name,
        count: formatNumber(reward.rewardsGiven)
      })
    }
  )
  return {
    length: rewards.length,
    data: array
  }
})

const dateHeadline = computed(() => {
  const date0 = formatDate({ date: dateValue.value[0], initialFormat: 'DD-MM-YYYY', toFormat: 'DD MMM YYYY' })
  const date1 = formatDate({ date: dateValue.value[1], initialFormat: 'DD-MM-YYYY', toFormat: 'DD MMM YYYY' })
  if (date0 === date1) {
    return date0
  } else {
    return `${date0} - ${date1}`
  }
})

const getDashboardStatistics = async (paramsUrl: dashboardStatisticsParamsInterface) => {
  const fetchAnalyticsDashboard = await axios.get('analytics/dashboard', { params: paramsUrl })
  dashboardItem.value = fetchAnalyticsDashboard.data.data
}

const fetchCharts = async () => {
  isLoaded.value = false
  await getDashboardStatistics({
    storeIds: selectedStore.value,
    fromDate: dateValue.value[0],
    toDate: dateValue.value[1]
  })

  isLoaded.value = true
}
const dateRangeChanged = () => {
  fetchCharts()
}
const getInStoreValue = (
  index: string,
  el: {
    title: string
    trend: string
    value: string
  }
) => {
  return dashboardItem.value?.inStore[index][el.value]
}
const getInStoreTrend = (
  index: string,
  el: {
    title: string
    trend: string
    value: string
  }
) => {
  return removeDecimals(dashboardItem.value?.inStore[index][el.trend], 2)
}
const handleStepper = (index: number) => {
  displayPongoInStoreChart.value = index
}
const removeDecimals = (number: string, decimals: number) => {
  return parseFloat(number).toFixed(decimals)
}

fetchCharts()
</script>
<template>
  <div class="relative">
    <PartnerModal />
    <div>
      <div>
        <UpsellBanner class="block md:hidden" />
        <div class="px-6 pt-6 sm:px-12 sm:pt-8">
          <LegacyDashboardBanner />
        </div>
        <TheListHeader :title="pageTitle.title" />
        <div class="mb-6 px-6 sm:px-12">
          <ThePageSettings
            v-model:date-range-value="dateValue"
            display-store-selector
            display-date-range-picker
            @store-selected="fetchCharts"
            @update:date-range-value="dateRangeChanged"
          />
        </div>
        <div class="px-6 pb-6 sm:px-12 sm:pb-12">
          <div class="flex flex-col items-center md:flex-row">
            <Headline :title="t('pages.dashboard.dashboardPage.subtitles.pongoInStore')" class="mb-3">
              <template #icon-left>
                <BaseIcon name="HeroCubeIconSolid" />
              </template>
            </Headline>
            <p class="mb-3 ml-2 text-foreground-secondary">
              {{ dateHeadline }}
            </p>
          </div>
          <div class="mb-10 grid grid-cols-6 gap-6">
            <!-- BEGIN Pongo in store -->
            <div class="col-span-6 overflow-hidden rounded-lg border bg-white xl:col-span-4">
              <div>
                <div
                  v-if="isLoaded && data.inStore && Object.keys(data.inStore).length > 0"
                  class="flex flex-col lg:flex-row"
                >
                  <ChartStepper
                    v-for="(inStoreLabel, inStoreLabelIndex, i) in inStoreLabels"
                    :key="inStoreLabelIndex"
                    :index="i"
                    :title="inStoreLabel.title"
                    :value="getInStoreValue(inStoreLabelIndex, inStoreLabel)"
                    :trend="getInStoreTrend(inStoreLabelIndex, inStoreLabel)"
                    :active="displayPongoInStoreChart === i"
                    @click="handleStepper(i)"
                  />
                </div>
                <section v-if="isLoaded">
                  <div v-if="displayPongoInStoreChart === 0">
                    <VisitorsPerDayChart />
                  </div>
                  <div v-if="displayPongoInStoreChart === 1">
                    <NewMembersChart />
                  </div>
                  <div v-if="displayPongoInStoreChart === 2">
                    <RewardsChart />
                  </div>
                </section>
                <section v-else class="relative">
                  <div class="flex flex-col gap-6 lg:flex-row">
                    <div v-for="i in 3" :key="i" class="px-10 py-6">
                      <p class="mb-2 h-3 w-28 bg-stone-200" placeholder-animation />
                      <p class="mb-2 h-5 w-28 bg-muted" placeholder-animation />
                      <p class="h-3 w-16 bg-muted" placeholder-animation />
                    </div>
                  </div>
                </section>
              </div>
            </div>
            <!-- END Pongo in store -->

            <!-- BEGIN hourly visits -->
            <div class="col-span-6 overflow-hidden rounded-lg border bg-white xl:col-span-2">
              <div v-if="isLoaded" class="space-y-6 px-4 py-6 sm:p-6">
                <h3 class="text-lg leading-6 text-foreground">
                  {{ t('pages.dashboard.dashboardPage.subtitles.hourlyVisitors') }}
                </h3>
                <HourlyVisits />
              </div>
              <section v-else class="relative h-44 p-6 md:h-72">
                <div class="h-3 w-28 bg-stone-200" placeholder-animation />
              </section>
            </div>
            <!-- END hourly visits -->
          </div>

          <div v-if="isLoaded" class="mb-5 grid grid-cols-6 gap-6">
            <!-- BEGIN Campaign card -->
            <div v-if="data?.smsCampaign" class="col-span-6 mb-8 lg:col-span-3 2xl:col-span-2">
              <Headline :title="t('pages.dashboard.dashboardPage.subtitles.campaign')" class="mb-3">
                <template #icon-left>
                  <BaseIcon name="HeroChatBubbleOvalLeftEllipsisIconSolid" />
                </template>
              </Headline>
              <Album
                v-if="data.smsCampaign?.campaignId"
                :title="t('pages.dashboard.dashboardPage.campaign.title')"
                :subtitle="data.smsCampaign.title"
                :left-legend="t('pages.dashboard.dashboardPage.campaign.converted')"
                :left-value="formatNumber(data.smsCampaign.customersConverted) ?? 0"
                :right-legend="t('pages.dashboard.dashboardPage.campaign.turnover')"
                :right-value="formatNumber(Math.round(data.smsCampaign.generatedTurnover), true)"
                :link="t('pages.dashboard.dashboardPage.campaign.link')"
                :truncate="true"
                @click="router.push({ name: 'CampaignDetail', params: { campaignId: data.smsCampaign.campaignId } })"
              />
              <Album
                v-else
                :title="t('pages.dashboard.dashboardPage.campaign.empty.title')"
                :subtitle="t('pages.dashboard.dashboardPage.campaign.empty.subtitle')"
                :link="t('pages.dashboard.dashboardPage.campaign.empty.link')"
                :no-data="true"
                :background-pattern="noData.pattern"
                background-gradient="bg-brand-gradient"
                :background-name="iconCampaignSms"
                @click="router.push({ name: 'CampaignCreate', query: { type: 'sms' } })"
              />
            </div>
            <!-- END Campaign card -->
            <!-- BEGIN game contest card -->
            <div v-if="data?.contestGame" class="col-span-6 mb-8 lg:col-span-3 2xl:col-span-2">
              <Headline :title="t('pages.dashboard.dashboardPage.subtitles.gameContest')" class="mb-3">
                <template #icon-left>
                  <BaseIcon name="HeroQrCodeIconSolid" />
                </template>
              </Headline>
              <Album
                v-if="data.contestGame?.contestGameId"
                :title="t('pages.dashboard.dashboardPage.contest.title')"
                :left-legend="t('pages.dashboard.dashboardPage.contest.participants')"
                :left-value="formatNumber(data.contestGame.participants)"
                :right-legend="t('pages.dashboard.dashboardPage.contest.rewards')"
                :right-value="formatNumber(data.contestGame.rewards)"
                :link="t('pages.dashboard.dashboardPage.contest.link')"
                @click="router.push({ name: 'ContestDetail', params: { contestId: data.contestGame.contestGameId } })"
              >
                <template #custom-subtitle>
                  <p class="text-foreground">
                    <span class="underline">{{ data.contestGame.title }}</span>
                    <span class="ml-3 mr-2 text-xs">🏠</span>
                    <span
                      >{{ data.contestGame.stores.length }}
                      {{ t('global.words.store', data.contestGame.stores.length) }}</span
                    >
                  </p>
                </template>
              </Album>
              <Album
                v-else
                :title="t('pages.dashboard.dashboardPage.contest.empty.title')"
                :subtitle="t('pages.dashboard.dashboardPage.contest.empty.subtitle')"
                :link="t('pages.dashboard.dashboardPage.contest.empty.link')"
                :no-data="true"
                background-gradient="bg-brand-gradient"
                :background-pattern="noData.pattern"
                :background-name="iconContest"
                @click="router.push({ name: 'ContestCreate' })"
              />
            </div>
            <!-- END game contest card -->

            <!-- BEGIN review card -->
            <div v-if="data?.reviews" class="col-span-6 mb-8 lg:col-span-3 2xl:col-span-2">
              <Headline :title="t('pages.dashboard.dashboardPage.subtitles.review')" class="mb-3">
                <template #icon-left>
                  <BaseIcon name="HeroStarIconSolid" />
                </template>
              </Headline>
              <Album
                v-if="data.reviews?.ratingValueAverage"
                :title="t('pages.dashboard.dashboardPage.review.title')"
                :left-legend="t('pages.dashboard.dashboardPage.review.repliedRate')"
                :left-value="data.reviews.replyRate ? `${removeDecimals(data.reviews.replyRate, 0)}%` : '0%'"
                :right-legend="t('pages.dashboard.dashboardPage.review.ratingAverage')"
                :right-value="removeDecimals(data.reviews.ratingValueAverage, 2)"
                :link="t('pages.dashboard.dashboardPage.review.link')"
                @click="router.push({ name: 'ReviewAnalytics' })"
              />
              <Album
                v-else
                :title="t('pages.dashboard.dashboardPage.review.empty.title')"
                :subtitle="t('pages.dashboard.dashboardPage.review.empty.subtitle')"
                :link="t('pages.dashboard.dashboardPage.review.empty.link')"
                :no-data="true"
                background-gradient="bg-brand-gradient"
                :background-pattern="noData.pattern"
                :background-name="iconReview"
                @click="router.push({ name: 'ReviewSettings' })"
              />
            </div>
            <!-- END review card -->

            <!-- BEGIN fidelity card -->
            <div v-if="data?.rewards" class="col-span-6 mb-8 lg:col-span-3 2xl:col-span-2">
              <Headline :title="t('pages.dashboard.dashboardPage.subtitles.fidelity')" class="mb-3">
                <template #icon-left>
                  <BaseIcon name="HeroGiftIconSolid" />
                </template>
              </Headline>
              <Album
                v-if="data.rewards.length > 0"
                :title="
                  t(
                    'pages.dashboard.dashboardPage.reward.title',
                    {
                      count: rewardsRanking.length
                    },
                    rewardsRanking.length
                  )
                "
                :ranking="rewardsRanking.data"
                :link="t('pages.dashboard.dashboardPage.reward.link')"
                @click="router.push({ name: 'RewardPage', params: { fromDate: dateValue[0], toDate: dateValue[1] } })"
              />
              <Album
                v-else
                :title="t('pages.dashboard.dashboardPage.reward.empty.title')"
                :subtitle="t('pages.dashboard.dashboardPage.reward.empty.subtitle')"
                :link="t('pages.dashboard.dashboardPage.reward.empty.link')"
                :no-data="true"
                background-gradient="bg-brand-gradient"
                :background-pattern="noData.pattern"
                :background-name="iconRewards"
                @click="router.push({ name: 'LoyaltyEdit' })"
              />
            </div>
            <!-- END fidelity card -->

            <!-- BEGIN survey card -->
            <div v-if="data?.surveys" class="col-span-6 mb-8 lg:col-span-3 2xl:col-span-2">
              <Headline :title="t('pages.dashboard.dashboardPage.subtitles.survey')" class="mb-3">
                <template #icon-left>
                  <BaseIcon name="HeroNewspaperIconSolid" />
                </template>
              </Headline>
              <Album
                v-if="data?.surveys?.questionText?.length > 0"
                :title="t('pages.dashboard.dashboardPage.survey.title')"
                :subtitle="data.surveys.questionText"
                :left-legend="t('pages.dashboard.dashboardPage.survey.rate')"
                :left-value="`${removeDecimals(data.surveys.surveyAnswerRate, 0)}%`"
                :link="t('pages.dashboard.dashboardPage.survey.link')"
                @click="router.push({ name: 'SurveyDetail', params: { surveyId: data.surveys.surveyId } })"
              />
              <Album
                v-else
                :title="t('pages.dashboard.dashboardPage.survey.empty.title')"
                :subtitle="t('pages.dashboard.dashboardPage.survey.empty.subtitle')"
                :link="t('pages.dashboard.dashboardPage.survey.empty.link')"
                :no-data="true"
                background-gradient="bg-brand-gradient"
                :background-pattern="noData.pattern"
                :background-name="iconSurvey"
                @click="router.push({ name: 'SurveyCreate' })"
              />
            </div>
            <!-- END survey card -->

            <!-- BEGIN autopilot card -->
            <div v-if="data?.autopilot" class="col-span-6 mb-8 lg:col-span-3 2xl:col-span-2">
              <Headline :title="t('pages.dashboard.dashboardPage.subtitles.autopilot')" class="mb-3">
                <template #icon-left>
                  <BaseIcon name="HeroBoltIconSolid" />
                </template>
              </Headline>
              <Album
                v-if="data.autopilot.length > 0"
                :title="t('pages.dashboard.dashboardPage.autopilot.title', autopilotsRanking.length)"
                :ranking="autopilotsRanking.data"
                :link="t('pages.dashboard.dashboardPage.autopilot.link')"
                @click="router.push({ name: 'AutopilotList' })"
              />
              <Album
                v-else
                :title="t('pages.dashboard.dashboardPage.autopilot.empty.title')"
                :subtitle="t('pages.dashboard.dashboardPage.autopilot.empty.subtitle')"
                :link="t('pages.dashboard.dashboardPage.autopilot.empty.link')"
                :no-data="true"
                background-gradient="bg-brand-gradient"
                :background-pattern="noData.pattern"
                :background-name="iconAutopilot"
                @click="router.push({ name: 'AutopilotList' })"
              />
            </div>
            <!-- END autopilot card -->
          </div>
          <div v-else class="mb-10 grid grid-cols-6 gap-8">
            <div v-for="i in 6" :key="i" class="col-span-6 lg:col-span-3 2xl:col-span-2">
              <div class="mb-2 flex items-center gap-2">
                <span class="relative h-5 w-5 bg-stone-200" placeholder-animation />
                <span class="relative h-3 w-32 bg-muted" placeholder-animation />
              </div>
              <div class="h-40 rounded-sm border bg-white px-3 py-6">
                <div class="relative mb-2 h-5 w-32 bg-stone-200" placeholder-animation />
                <div class="relative h-3 w-48 bg-muted" placeholder-animation />
              </div>
            </div>
          </div>
          <DashboardBanner />
        </div>
      </div>
    </div>
  </div>
</template>
